/**
 * Writes for the feature rollout panel — per-organization and bulk
 * (LOCKER-408 DoD3, DoD5, DoD6).
 *
 * The mutation shape follows `SaTenantFeatures.tsx`, which already had a
 * working toggle: create a FeatureAssignment when the tenant has none, patch
 * the existing one otherwise. What is new here is the failure path. Every
 * mutation on this page previously ended in `console.error`, so a rejected
 * write left the list unchanged and the operator with no way to tell a
 * successful no-op from a silent failure.
 *
 * State is keyed by tenantId so one in-flight write does not disable the other
 * rows, and a failure marks exactly the row that failed.
 */
import { useCallback, useState } from "react"
import { Feature } from "shared/feature"
import { FeatureAssignmentThingTypeMrn } from "shared/featureAssignmentData"
import {
  useSaCreateThingsMutation,
  useSaUpdateThingsMutation,
} from "src/features/things/thingsSaApi"
import {
  FeatureRolloutAction,
  FeatureRolloutRow,
  wouldChange,
} from "./featureRolloutModel"

/**
 * One assignment write, as the `/things` endpoints accept it.
 *
 * A create omits `id` and carries the feature keys; a patch carries `id` and
 * only the fields it changes. Both go to the same endpoint, whose own arg type
 * is `{ body: Record<string, any> }` (thingsSaApi.ts), so this shape is not
 * enforced at the boundary — but it is the shape `buildPayloads` produces, and
 * naming it keeps `attempt` from having to say `any[]`.
 */
export type FeatureAssignmentWrite = {
  thingTypeMrn: string
  tenantId: string
  isActivated: boolean
  activatedAt?: string
  /** Present on a patch, absent on a create. */
  id?: string
  featureId?: string
  featureRefName?: string
}

/** A failed write, kept so the row can explain itself and offer a retry. */
export type FeatureWriteFailure = {
  action: FeatureRolloutAction
  message: string
  /** The rows the failed attempt covered — what a retry re-attempts. */
  tenantIds: string[]
}

const errorMessage = (e: any): string =>
  e?.data?.message ||
  e?.data?.error ||
  e?.error ||
  e?.message ||
  "Unknown error"

/**
 * Build the create/patch payloads for a set of rows.
 *
 * `activatedAt` is stamped on activation only, matching the existing
 * SaTenantFeatures write. `activatedBy` stays unpopulated — surfacing who
 * changed a row is LOCKER-408 OOS1.
 */
const buildPayloads = (
  rows: FeatureRolloutRow[],
  feature: Feature,
  action: FeatureRolloutAction,
) => {
  const isActivated = action === "activate"
  const nowIso = new Date().toISOString()
  const toCreate: FeatureAssignmentWrite[] = []
  const toUpdate: FeatureAssignmentWrite[] = []

  rows.forEach((row) => {
    if (row.assignment) {
      toUpdate.push({
        id: row.assignment.id,
        thingTypeMrn:
          row.assignment.thingTypeMrn || FeatureAssignmentThingTypeMrn,
        tenantId: row.tenantId,
        isActivated,
        activatedAt: isActivated ? nowIso : undefined,
      })
    } else {
      toCreate.push({
        thingTypeMrn: FeatureAssignmentThingTypeMrn,
        tenantId: row.tenantId,
        featureId: feature.id,
        featureRefName: feature.refName,
        isActivated,
        activatedAt: isActivated ? nowIso : undefined,
      })
    }
  })

  return { toCreate, toUpdate }
}

export const useFeatureAssignmentWrites = (feature: Feature) => {
  const [createThings] = useSaCreateThingsMutation()
  const [updateThings] = useSaUpdateThingsMutation()

  /** tenantId -> true while its write is in flight. */
  const [pending, setPending] = useState<Record<string, boolean>>({})
  /** tenantId -> the failure to surface on that row. */
  const [failures, setFailures] = useState<Record<string, FeatureWriteFailure>>(
    {},
  )

  const markPending = useCallback((tenantIds: string[], value: boolean) => {
    setPending((prev) => {
      const next = { ...prev }
      tenantIds.forEach((id) => {
        if (value) next[id] = true
        else delete next[id]
      })
      return next
    })
  }, [])

  const clearFailures = useCallback((tenantIds: string[]) => {
    setFailures((prev) => {
      const next = { ...prev }
      tenantIds.forEach((id) => delete next[id])
      return next
    })
  }, [])

  /**
   * Apply `action` to `rows`.
   *
   * Rows already in the target state are dropped first, so a bulk action never
   * PATCHes a row it would not change. Resolves `true` when everything the call
   * attempted succeeded.
   *
   * On rejection the rows are marked failed and NOT optimistically shown as
   * changed — RTK Query is the single source of truth for row state here, so a
   * failed write leaves the row reading its stored value with a failure notice
   * beside it. That is the "reverts" half of DoD6: there is no local override
   * left asserting a state the backend refused.
   *
   * A bulk action can need BOTH a create and a patch, and they are two separate
   * requests that can succeed independently. Each is therefore attributed to
   * its own rows: the first version wrapped both in one try and recorded the
   * failure against the union, so a landed create was reported as failed
   * because a later patch was rejected (round 1, F1.2). Both are attempted even
   * if the first fails — the rows are independent, and skipping the second
   * would strand rows the operator asked to change for no reason.
   */
  const applyAction = useCallback(
    async (
      rows: FeatureRolloutRow[],
      action: FeatureRolloutAction,
    ): Promise<boolean> => {
      const targets = rows.filter((r) => wouldChange(r, action))
      if (targets.length === 0) return true

      const tenantIds = targets.map((r) => r.tenantId)
      clearFailures(tenantIds)
      markPending(tenantIds, true)

      const { toCreate, toUpdate } = buildPayloads(targets, feature, action)

      /** Send one group, and record any failure against ONLY that group. */
      const attempt = async (
        things: FeatureAssignmentWrite[],
        send: (arg: { body: { things: FeatureAssignmentWrite[] } }) => {
          unwrap: () => Promise<unknown>
        },
      ): Promise<boolean> => {
        if (things.length === 0) return true
        try {
          await send({ body: { things } }).unwrap()
          return true
        } catch (e) {
          const groupIds = things.map((t) => t.tenantId)
          const failure: FeatureWriteFailure = {
            action,
            message: errorMessage(e),
            tenantIds: groupIds,
          }
          setFailures((prev) => {
            const next = { ...prev }
            groupIds.forEach((id) => {
              next[id] = failure
            })
            return next
          })
          return false
        }
      }

      try {
        // Sequential rather than concurrent: both calls hit the same /things
        // endpoint, and an ordered pair is easier to reason about in a log.
        const created = await attempt(toCreate, createThings)
        const updated = await attempt(toUpdate, updateThings)
        return created && updated
      } finally {
        markPending(tenantIds, false)
      }
    },
    [createThings, updateThings, feature, clearFailures, markPending],
  )

  /** Flip one row to the opposite of its resolved state. */
  const toggleRow = useCallback(
    (row: FeatureRolloutRow) =>
      applyAction([row], row.granted ? "deactivate" : "activate"),
    [applyAction],
  )

  /**
   * Re-attempt a failed write.
   *
   * Re-derives the rows from CURRENT state rather than replaying the old
   * payload, so a retry after someone else changed the row does the right thing
   * for where the row is now.
   *
   * The failure's WHOLE tenant set is cleared up front, not just the rows the
   * retry will write. `applyAction` drops rows already in the target state and
   * returns early when none are left, so keying the clear to its targets left a
   * stale notice on a row whose write had in fact landed — removable only by
   * reloading the page (round 1, F1.2).
   */
  const retry = useCallback(
    (failure: FeatureWriteFailure, currentRows: FeatureRolloutRow[]) => {
      const ids = new Set(failure.tenantIds)
      clearFailures(failure.tenantIds)
      return applyAction(
        currentRows.filter((r) => ids.has(r.tenantId)),
        failure.action,
      )
    },
    [applyAction, clearFailures],
  )

  return {
    applyAction,
    toggleRow,
    retry,
    /** True while THIS row's write is in flight. */
    isPending: (tenantId: string) => !!pending[tenantId],
    /** The failure to show on THIS row, if any. */
    failureFor: (tenantId: string) => failures[tenantId],
    /** Any write in flight — for the panel-level busy indicator only. */
    isBusy: Object.keys(pending).length > 0,
    failureCount: Object.keys(failures).length,
    clearFailures,
  }
}
